import { Component, HostListener, ViewChild, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { IAppDataModel, IYearData, YearData } from '@app/@appstate/appdata.model';

import { AppDataQuery } from '@app/@appstate/appdata.query';
import { AppDataService } from '@app/@appstate/appdata.service';
import { AuthQuery } from '@app/auth/auth.query';
import { AuthDataService } from '@app/auth/auth-data.service';

import { Observable } from 'rxjs';

import { ConfirmationService } from 'primeng/api';

import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { MatDialog, MAT_DIALOG_DATA, MatDialogConfig } from '@angular/material/dialog';

import { JournalService } from '@app/@core';
import {
  StammdatenService,
  Artikel,
  ArtikelStammdaten,
  Massnahme,
  Abweichung,
} from '@app/services/stammdaten.service';

import { DialogInputComponent } from '../@shared/dialog/input/dialog-input.component';
import { ConfirmDialogComponent } from '../@shared/dialog/confirm-dialog/confirm-dialog.component';

import { ArtikelDialogComponent } from './dialog-artikel/dialog-artikel.component';
import { DialogMassnahmeComponent } from './dialog-massnahme/dialog-massnahme.component';
import { DialogAbweichungComponent } from './dialog-abweichung/dialog-abweichung.component';

// data structures
interface ArtikelExt extends Artikel {
  isEditable?: boolean;
}

interface MassnahmeExt extends Massnahme {
  isEditable?: boolean;
}

interface AbweichungExt extends Abweichung {
  isEditable?: boolean;
}

/**
 * component
 *
 */
@Component({
  selector: 'app-stammdaten',
  templateUrl: './stammdaten.component.html',
  styleUrls: ['./stammdaten.component.css'],
})
export class StammdatenComponent implements OnInit {
  // the state
  appData: IAppDataModel;

  // copy of the current state
  cAppData: IAppDataModel;

  arMonate = [];

  changePwdForm!: UntypedFormGroup;

  // Display columns
  displayArtikel: string[] = ['nummer', 'beschreibung', 'art', 'basiseinheit', 'code', 'action'];
  displayMassnahme: string[] = ['beschreibung', 'druck_auf_bericht', 'foto_erforderlich', 'sortierung', 'action'];
  displayAbweichung: string[] = ['caption', 'description', 'status', 'sortierung', 'action'];
  
  // Data sources
  artikelStammdaten: ArtikelStammdaten;
  dataSourceArtikel: MatTableDataSource<ArtikelExt> = new MatTableDataSource<ArtikelExt>();
  dataSourceMassnahme: MatTableDataSource<MassnahmeExt> = new MatTableDataSource<MassnahmeExt>();
  dataSourceAbweichung: MatTableDataSource<AbweichungExt> = new MatTableDataSource<AbweichungExt>();

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _adQuery: AppDataQuery,
    private _adService: AppDataService,
    private authQuery: AuthQuery,
    private authDataService: AuthDataService,
    private stammdatenService: StammdatenService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private confirmationService: ConfirmationService,
    private journalService: JournalService
  ) {}

  ngOnInit(): void {
    // query and init the data
    this._adQuery.selectEntity(this.authQuery.appid).subscribe((state) => {
      this.appData = <IAppDataModel>state;

      // deep clone
      try {
        this.cAppData = JSON.parse(JSON.stringify(state));
      } catch (err) {
        // console.log("error");
      }

      //console.log("change state");
    });

    // get the stammdaten
    this.getArtikelStammdaten();
    this.getArtikels();
    this.getMassnahmen();
    this.getAbweichungen();
  }

  /**
   * get the artikel stammdaten
   */
  getArtikelStammdaten() {
    console.log('Loading artikel stammdaten...');
    this.stammdatenService.getArtikelStammdaten().subscribe({
      next: (response) => {
        console.log('Artikel stammdaten loaded:', response);
        this.artikelStammdaten = response;
      },
      error: (error) => {
        console.error('Error loading artikel stammdaten:', error);
        console.error('Error details:', error);
        this.snackBar.open('Fehler beim Laden der Artikel-Stammdaten', 'Schließen', { duration: 3000 });
      }
    });
  }

  /**
   * Get all artikels (actual artikel records)
   */
  getArtikels() {
    console.log('Loading artikels...');
    this.stammdatenService.getArtikels().subscribe({
      next: (response) => {
        console.log('Artikels loaded:', response);
        console.log('Artikels data length:', response?.length);
        this.dataSourceArtikel.data = response || [];
      },
      error: (error) => {
        console.error('Error loading artikels:', error);
        console.error('Error details:', error);
        this.snackBar.open('Fehler beim Laden der Artikel', 'Schließen', { duration: 3000 });
      }
    });
  }

  /**
   * sync artikel stammdaten
   */
  syncArtikelStammdaten() {
    console.log('Syncing artikel stammdaten...');
    this.stammdatenService.syncArtikelStammdaten(this.artikelStammdaten).subscribe({
      next: (response) => {
        console.log('Artikel stammdaten synced:', response);
        this.snackBar.open('Artikel-Stammdaten erfolgreich gespeichert', 'Schließen', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error syncing artikel stammdaten:', error);
        this.snackBar.open('Fehler beim Speichern der Artikel-Stammdaten', 'Schließen', { duration: 3000 });
      }
    });
  }

  /**
   * add new artikel
   */
  addNewArtikel() {
    const dialogRef = this.dialog.open(ArtikelDialogComponent, {
      width: '600px',
      data: { mode: 'create' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('New artikel created:', result);
        this.getArtikels(); // Reload data
        this.snackBar.open('Artikel erfolgreich erstellt', 'Schließen', { duration: 3000 });
      }
    });
  }

  /**
   * edit item
   */
  editItem(type: string, item: any) {
    if (type === 'Artikel') {
      const dialogRef = this.dialog.open(ArtikelDialogComponent, {
        width: '600px',
        data: { mode: 'edit', artikel: item }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          console.log('Artikel updated:', result);
          this.getArtikels(); // Reload data
          this.snackBar.open('Artikel erfolgreich aktualisiert', 'Schließen', { duration: 3000 });
        }
      });
    } else if (type === 'Massnahme') {
      const dialogRef = this.dialog.open(DialogMassnahmeComponent, {
        width: '600px',
        data: { mode: 'edit', massnahme: item }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          console.log('Massnahme updated:', result);
          this.getMassnahmen();
          this.snackBar.open('Maßnahme erfolgreich aktualisiert', 'Schließen', { duration: 3000 });
        }
      });
    } else if (type === 'Abweichung') {
      const dialogRef = this.dialog.open(DialogAbweichungComponent, {
        width: '600px',
        data: { mode: 'edit', abweichung: item }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          console.log('Abweichung updated:', result);
          this.getAbweichungen();
          this.snackBar.open('Abweichung erfolgreich aktualisiert', 'Schließen', { duration: 3000 });
        }
      });
    }
  }

  /**
   * remove artikel item
   */
  removeItem(type: string, item: any) {
    if (type === 'Artikel') {
      this.confirmationService.confirm({
        message: 'Sind Sie sicher, dass Sie diesen Artikel löschen möchten?',
        accept: () => {
          this.stammdatenService.deleteArtikel(item.id).subscribe({
            next: () => {
              this.snackBar.open('Artikel erfolgreich gelöscht', 'Schließen', { duration: 3000 });
              this.getArtikels();
            },
            error: (error) => {
              console.error('Error deleting artikel:', error);
              this.snackBar.open('Fehler beim Löschen des Artikels', 'Schließen', { duration: 3000 });
            }
          });
        }
      });
    } else if (type === 'Massnahme') {
      this.confirmationService.confirm({
        message: 'Sind Sie sicher, dass Sie diese Maßnahme löschen möchten?',
        accept: () => {
          this.stammdatenService.deleteMassnahme(item.id).subscribe({
            next: () => {
              this.snackBar.open('Maßnahme erfolgreich gelöscht', 'Schließen', { duration: 3000 });
              this.getMassnahmen();
            },
            error: (error) => {
              console.error('Error deleting massnahme:', error);
              this.snackBar.open('Fehler beim Löschen der Maßnahme', 'Schließen', { duration: 3000 });
            }
          });
        }
      });
    } else if (type === 'Abweichung') {
      this.confirmationService.confirm({
        message: 'Sind Sie sicher, dass Sie diese Abweichung löschen möchten?',
        accept: () => {
          this.stammdatenService.deleteAbweichung(item.id).subscribe({
            next: () => {
              this.snackBar.open('Abweichung erfolgreich gelöscht', 'Schließen', { duration: 3000 });
              this.getAbweichungen();
            },
            error: (error) => {
              console.error('Error deleting abweichung:', error);
              this.snackBar.open('Fehler beim Löschen der Abweichung', 'Schließen', { duration: 3000 });
            }
          });
        }
      });
    }
  }

  /**
   * Get all massnahmen
   */
  getMassnahmen() {
    console.log('Loading massnahmen...');
    this.stammdatenService.getMassnahmen().subscribe({
      next: (response) => {
        console.log('Massnahmen loaded:', response);
        console.log('Massnahmen data length:', response?.length);
        this.dataSourceMassnahme.data = response || [];
      },
      error: (error) => {
        console.error('Error loading massnahmen:', error);
        console.error('Error details:', error);
        this.snackBar.open('Fehler beim Laden der Maßnahmen', 'Schließen', { duration: 3000 });
      }
    });
  }

  /**
   * Get all abweichungen
   */
  getAbweichungen() {
    console.log('Loading abweichungen...');
    this.stammdatenService.getAbweichungen().subscribe({
      next: (response) => {
        console.log('Abweichungen loaded:', response);
        console.log('Abweichungen data length:', response?.length);
        this.dataSourceAbweichung.data = response || [];
      },
      error: (error) => {
        console.error('Error loading abweichungen:', error);
        console.error('Error details:', error);
        this.snackBar.open('Fehler beim Laden der Abweichungen', 'Schließen', { duration: 3000 });
      }
    });
  }

  /**
   * Add new massnahme
   */
  addNewMassnahme() {
    const dialogRef = this.dialog.open(DialogMassnahmeComponent, {
      width: '600px',
      data: { mode: 'create' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('New massnahme created:', result);
        this.getMassnahmen();
        this.snackBar.open('Maßnahme erfolgreich erstellt', 'Schließen', { duration: 3000 });
      }
    });
  }

  /**
   * Add new abweichung
   */
  addNewAbweichung() {
    const dialogRef = this.dialog.open(DialogAbweichungComponent, {
      width: '600px',
      data: { mode: 'create' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('New abweichung created:', result);
        this.getAbweichungen();
        this.snackBar.open('Abweichung erfolgreich erstellt', 'Schließen', { duration: 3000 });
      }
    });
  }
}
